// apps/frontend/src/components/wow/LikeWowMoment.tsx
//
// Moment WOW Glowp : quand l'acheteuse like sa premiere piece d'une
// session, Glowp lui glisse une bulle douce avec 2-3 pieces similaires.
// Pas un pop-up commercial, pas push : un signe discret que Glowp ecoute.
//
// Memory feedback_silence_precision : apparait 1 fois par session, dismiss
// definitif possible. Aucun spam.
//
// Branche sur le custom event "glowp:product-liked" dispatch depuis
// ProductCard quand un like = ADD. Decoulee du chemin de like.

import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import apiClient from '../../services/apiClient';

const SESSION_FLAG = 'glowp_like_wow_shown';
const AUTO_DISMISS_MS = 12_000;

interface SimilarProduct {
  id: string;
  slug: string;
  title: string;
  price: number | string;
  image: string | null;
}

const LikeWowMoment = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [products, setProducts] = useState<SimilarProduct[]>([]);
  const [closing, setClosing] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleLike = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { productId?: string };
      const productId = detail?.productId;
      if (!productId) return;

      // 1 fois par session uniquement
      try {
        if (sessionStorage.getItem(SESSION_FLAG) === '1') return;
      } catch {
        return;
      }
      if (fetchedRef.current) return;
      fetchedRef.current = true;

      try {
        const res = await apiClient.get<{
          data?: { products?: SimilarProduct[] } | SimilarProduct[];
        }>(`/api/v1/products/${productId}/similar?limit=3`);
        const payload = res.data?.data;
        const list = Array.isArray(payload)
          ? payload
          : (payload?.products ?? []);
        if (list.length === 0) {
          // Pas de similaires : on ne montre rien plutot qu'une bulle vide
          fetchedRef.current = false;
          return;
        }
        // Normalise format products : title/image peuvent venir avec
        // des shapes variables selon le backend.
        const normalized = list.slice(0, 3).map((p: any) => ({
          id: p.id,
          slug: p.slug || p.id,
          title: p.title || p.product_translations?.[0]?.title || 'Sans titre',
          price: p.price ?? p.price_amount ?? 0,
          image: p.image || p.image_url || p.product_images?.[0]?.url || null,
        }));
        setProducts(normalized);
        setVisible(true);
        try {
          sessionStorage.setItem(SESSION_FLAG, '1');
        } catch {
          /* ignore */
        }
      } catch {
        fetchedRef.current = false;
      }
    };

    window.addEventListener('glowp:product-liked', handleLike);
    return () => window.removeEventListener('glowp:product-liked', handleLike);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = window.setTimeout(() => handleClose(), AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [visible]);

  const handleClose = () => {
    setClosing(true);
    window.setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 200);
  };

  const handleProductClick = (slug: string) => {
    navigate(`/product/${slug}`);
    handleClose();
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed bottom-20 sm:bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:right-6 sm:translate-x-0 z-[80] w-[calc(100%-24px)] max-w-[340px] transition-all duration-200 ${
        closing
          ? 'opacity-0 translate-y-2'
          : 'opacity-100 translate-y-0 animate-slide-up'
      }`}
    >
      <div className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(200,168,130,0.25)] border border-[#e8e2d4] overflow-hidden">
        {/* Header */}
        <div className="px-4 pt-3.5 pb-2 flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#C8A882] to-[#B89968] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-[#2d2a26] leading-snug">
              Tu aimes cette pièce ?
            </p>
            <p className="text-[12px] text-[#7d7060] mt-0.5 leading-snug">
              On a vu autre chose pour toi.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fermer"
            className="text-[#a89a85] hover:text-[#2d2a26] transition-colors flex-shrink-0 -mt-0.5 -mr-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Thumbnails */}
        <div className="px-3 pb-3 grid grid-cols-3 gap-2">
          {products.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => handleProductClick(p.slug)}
              className="group text-left"
            >
              <div className="aspect-[3/4] w-full rounded-xl bg-[#FBF8F4] border border-[#e8e2d4] overflow-hidden">
                {p.image ? (
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-[#a89a85]" />
                  </div>
                )}
              </div>
              <p className="text-[11px] font-semibold text-[#C8A882] mt-1.5">
                {typeof p.price === 'number'
                  ? p.price.toFixed(2)
                  : String(p.price)}{' '}
                €
              </p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LikeWowMoment;
